/**
 * Odino's LLM-config PICKER (ADR-0071). Lives as a panel in the unified Settings
 * page (`/settings`) — Odino's home is Impostazioni and nowhere else.
 *
 * It used to be a provider/model/api-key FORM that created-or-updated the instance
 * DEFAULT config: this page authored the default while wearing Odino's name (the
 * mirror of the retired `app_assistant`). Configs are authored in **Configurazioni
 * LLM**; here you only choose which one Odino uses. On a fresh instance there is
 * nothing to choose, and it says so instead of quietly minting a default.
 *
 * Controlled + self-saving; the parent supplies the loaded `settings` and gets the
 * fresh ones back via `onSaved`.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Combobox } from "@/components/ui/combobox";
import { type OdinoSettings, odinoApi } from "@/lib/api-odino";

/** The sentinel for "no binding" — a Combobox value is a string, and "" reads as
 *  unset/placeholder, so the inherit option needs a name of its own. */
const INHERIT = "__inherit__";

export function OdinoSettingsForm({
  settings,
  onSaved,
}: {
  settings: OdinoSettings;
  onSaved: (s: OdinoSettings) => void;
}) {
  const { t } = useTranslation();
  const [configId, setConfigId] = useState(settings.config_id ?? INHERIT);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [savedOk, setSavedOk] = useState(false);

  const dirty = configId !== (settings.config_id ?? INHERIT);

  const save = async () => {
    setSaving(true);
    setErr(null);
    setSavedOk(false);
    try {
      const out = await odinoApi.putSettings({
        config_id: configId === INHERIT ? null : configId,
      });
      setSavedOk(true);
      onSaved(out);
    } catch {
      setErr(t("settings.odino.saveError", "Impossibile salvare. Riprova."));
    } finally {
      setSaving(false);
    }
  };

  if (settings.configs.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {t(
          "settings.odino.noConfigs",
          "Nessuna configurazione LLM. Creane una in Configurazioni LLM, poi torna qui per assegnarla a Odino.",
        )}{" "}
        <Link to="/ai/llm-configs" className="text-accent underline">
          {t("settings.odino.goToConfigs", "Vai a Configurazioni LLM")}
        </Link>
      </p>
    );
  }

  const defaultCfg = settings.configs.find((c) => c.is_default);
  const options = [
    {
      value: INHERIT,
      label: t("settings.odino.inherit", "Predefinita dell'istanza"),
      description: defaultCfg
        ? `${defaultCfg.name} · ${defaultCfg.provider} · ${defaultCfg.model}`
        : t("settings.odino.noDefault", "Nessuna predefinita impostata"),
    },
    ...settings.configs.map((c) => ({
      value: c.id,
      label: c.name,
      description: `${c.provider} · ${c.model}`,
    })),
  ];

  return (
    <div className="space-y-3">
      <div className="max-w-md space-y-1">
        <label className="text-xs text-muted-foreground" htmlFor="odino-config">
          {t("settings.odino.config", "Configurazione LLM")}
        </label>
        <Combobox
          id="odino-config"
          value={configId}
          onChange={(v) => setConfigId(v ?? INHERIT)}
          options={options}
        />
        <p className="text-xs text-muted-foreground">
          {t(
            "settings.odino.configHint",
            "Provider, modello e chiave si gestiscono in Configurazioni LLM. Qui scegli solo quale usa Odino.",
          )}
        </p>
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          className="rounded-md bg-accent px-3 py-1.5 text-sm text-accent-foreground disabled:opacity-50"
          onClick={() => void save()}
          disabled={saving || !dirty}
        >
          {saving ? t("common.saving", "Salvataggio…") : t("common.save", "Salva")}
        </button>
        {savedOk && !dirty && (
          <span className="text-xs text-success">{t("common.saved", "Salvato")}</span>
        )}
        {err && <span className="text-xs text-danger">{err}</span>}
      </div>
    </div>
  );
}
